import { ReactNode, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { PeriodBucket } from '../../../utils/reunionMensuelle';
import { ScopeColumn } from './ScopeSelector';
import { ReunionTransaction } from '../../../hooks/useReunionMensuelleData';
import SalesListModal, { ExtraColumn } from './SalesListModal';
import InfoTooltip from './InfoTooltip';

const SERIES_COLORS = ['#6366f1', '#10b981', '#f43f5e', '#f59e0b', '#0ea5e9', '#a855f7', '#14b8a6', '#ec4899'];

const COMPANY_COLORS: Record<string, string> = {
  KI: '#eab308',
  KA: '#22c55e',
  DRYADE: '#f97316',
};

function getSeriesColor(key: string, index: number): string {
  return COMPANY_COLORS[key] ?? SERIES_COLORS[index % SERIES_COLORS.length];
}

interface TrendChartCardProps {
  icon: ReactNode;
  iconBg: string;
  title: string;
  periods: PeriodBucket[];
  columns: ScopeColumn[];
  data: Map<string, Map<string, number | null>>;
  valueFormatter: (value: number) => string;
  valueSuffix?: string;
  getTransactions: (periodKey: string, columnKey: string) => ReunionTransaction[];
  infoText?: string;
  extraColumns?: ExtraColumn[];
  /** Calcule la valeur individuelle (ex : délai en jours) pour une transaction, afin de l'afficher et de la surligner dans la modal de détail. */
  getMetricValue?: (tx: ReunionTransaction) => number | null;
  metricLabel?: string;
}

function CustomTooltip({ active, payload, label, valueFormatter, valueSuffix }: any) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-lg px-3.5 py-2.5 text-xs">
      <p className="font-semibold text-slate-700 mb-1.5">{label}</p>
      <div className="space-y-1">
        {payload.map((entry: any) => (
          <div key={entry.dataKey} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="text-slate-500">{entry.name}</span>
            <span className="ml-auto font-semibold text-slate-800">
              {entry.value === null || entry.value === undefined ? '—' : `${valueFormatter(entry.value)}${valueSuffix}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TrendChartCard({
  icon, iconBg, title, periods, columns, data, valueFormatter, valueSuffix = '', getTransactions, infoText, extraColumns,
  getMetricValue, metricLabel,
}: TrendChartCardProps) {
  const [modalCell, setModalCell] = useState<{ periodKey: string; periodLabel: string; columnKey: string; columnLabel: string } | null>(null);

  const chartData = periods.map(p => {
    const row: Record<string, any> = { period: p.label };
    columns.forEach(c => {
      const v = data.get(p.key)?.get(c.key);
      row[c.key] = v ?? null;
    });
    return row;
  });

  const handleCellClick = (periodKey: string, periodLabel: string, columnKey: string, columnLabel: string) => {
    setModalCell({ periodKey, periodLabel, columnKey, columnLabel });
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center shadow-sm`}>{icon}</div>
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {infoText && <InfoTooltip text={infoText} />}
      </div>

      <div className="p-5 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }} barGap={4} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
            <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip valueFormatter={valueFormatter} valueSuffix={valueSuffix} />} cursor={{ fill: '#f1f5f9' }} />
            <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
            {columns.map((c, i) => (
              <Bar
                key={c.key}
                dataKey={c.key}
                name={c.label}
                fill={getSeriesColor(c.key, i)}
                radius={[5, 5, 0, 0]}
                cursor="pointer"
                onClick={(_: any, index: number) => handleCellClick(periods[index].key, periods[index].label, c.key, c.label)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto border-t border-slate-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50">
              <th className="px-4 py-2 text-left font-semibold text-slate-500 text-xs uppercase tracking-wide">Période</th>
              {columns.map((c, i) => (
                <th key={c.key} className="px-4 py-2 text-right font-semibold text-xs uppercase tracking-wide">
                  <span className="inline-flex items-center gap-1.5 justify-end w-full">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: getSeriesColor(c.key, i) }} />
                    <span className="text-slate-500">{c.label}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map(p => (
              <tr key={p.key} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                <td className="px-4 py-2 text-slate-600">{p.label}</td>
                {columns.map(c => {
                  const v = data.get(p.key)?.get(c.key);
                  const hasValue = v !== null && v !== undefined;
                  return (
                    <td
                      key={c.key}
                      onClick={hasValue ? () => handleCellClick(p.key, p.label, c.key, c.label) : undefined}
                      className={`px-4 py-2 text-right font-medium ${hasValue ? 'text-indigo-600 cursor-pointer hover:underline' : 'text-slate-700'}`}
                    >
                      {hasValue ? `${valueFormatter(v as number)}${valueSuffix}` : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalCell && (
        <SalesListModal
          title={`${title} — ${modalCell.periodLabel} — ${modalCell.columnLabel}`}
          transactions={getTransactions(modalCell.periodKey, modalCell.columnKey)}
          onClose={() => setModalCell(null)}
          extraColumns={extraColumns}
          metricColumn={getMetricValue ? {
            label: metricLabel ?? title,
            getValue: getMetricValue,
            average: data.get(modalCell.periodKey)?.get(modalCell.columnKey) ?? null,
            formatValue: (v: number) => `${valueFormatter(v)}${valueSuffix}`,
          } : undefined}
        />
      )}
    </div>
  );
}
